import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { fmtIDR, apiErr, isInternal } from "@/lib/api";
import { DateInput, fmtDate } from "@/components/DateInput";
import Receivables from "@/pages/Receivables";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileType2, Search, X, Wallet, Users as UsersIcon, Package, Settings as SettingsIcon, FileSpreadsheet, TrendingUp, Image as ImageIcon, Boxes, Upload, Download, ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { InvoiceImportModal } from "@/components/InvoiceImportModal";
import { Checkbox } from "@/components/ui/checkbox";
import { fl } from "@/lib/finI18n";
import { InvoiceTermBox } from "@/components/invoice/InvoiceTermBox";
import { InvoiceBulkBar } from "@/components/invoice/InvoiceBulkBar";
import { exportRows, useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { MasterImportModal } from "@/components/MasterImportModal";
import { ProductionTab } from "@/components/invoice/ProductionTab";
import { StockCardDialog } from "@/components/invoice/StockCardDialog";
import { invoiceFileName, termLabel, saveBlob } from "@/components/invoice/invoiceUtils";

const STATUS_META = {
  unpaid: { key: "inv.statusUnpaid", cls: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300" },
  partial: { key: "inv.statusPartial", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
  paid: { key: "inv.statusPaid", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
};

export default function Invoices() {
  const { user, selectedClientId, clients } = useAuth();
  const { t, lang } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const client = clients.find((c) => c.id === clientId);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [settings, setSettings] = useState({ logo: "", footer_note: "" });

  const loadRefs = useCallback(async () => {
    if (!clientId) return;
    const [c, p, b, s] = await Promise.all([
      api.get(`/invoice-customers?client_id=${clientId}`),
      api.get(`/invoice-products?client_id=${clientId}`),
      api.get(`/banks?client_id=${clientId}`),
      api.get(`/invoice-settings?client_id=${clientId}`),
    ]);
    setCustomers(c.data); setProducts(p.data); setBanks(b.data);
    setSettings(s.data || { logo: "", footer_note: "" });
  }, [clientId]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="page-sticky">
        <h1 className="font-head text-2xl font-bold tracking-tight">{t("inv.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("inv.desc")} {client && `· ${client.name}`}</p>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="invoices" data-testid="inv-tab-invoices"><FileSpreadsheet className="h-4 w-4 mr-1.5" />{t("inv.tabInvoices")}</TabsTrigger>
          <TabsTrigger value="customers" data-testid="inv-tab-customers"><UsersIcon className="h-4 w-4 mr-1.5" />{t("inv.tabCustomers")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="inv-tab-products"><Package className="h-4 w-4 mr-1.5" />{t("inv.tabProducts")}</TabsTrigger>
          <TabsTrigger value="production" data-testid="inv-tab-production"><Boxes className="h-4 w-4 mr-1.5" />{fl("production", lang)}</TabsTrigger>
          <TabsTrigger value="receivables" data-testid="inv-tab-receivables"><TrendingUp className="h-4 w-4 mr-1.5" />{t("inv.tabReceivables")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="inv-tab-settings"><SettingsIcon className="h-4 w-4 mr-1.5" />{t("inv.tabSettings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <InvoiceListTab clientId={clientId} client={client} customers={customers} products={products} banks={banks} settings={settings} onRefsReload={loadRefs} />
        </TabsContent>
        <TabsContent value="customers">
          <CustomersTab clientId={clientId} customers={customers} onDone={loadRefs} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsTab clientId={clientId} products={products} onDone={loadRefs} />
        </TabsContent>
        <TabsContent value="production">
          <ProductionTab clientId={clientId} products={products} onDone={loadRefs} />
        </TabsContent>
        <TabsContent value="receivables">
          <Receivables embedded />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab clientId={clientId} settings={settings} banks={banks} onDone={loadRefs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InvoiceListTab({ clientId, client, customers, products, banks, settings, onRefsReload }) {
  const { t, lang } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    if (!clientId) return;
    const p = new URLSearchParams({ client_id: clientId });
    if (status !== "all") p.set("status", status);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    if (q) p.set("q", q);
    const { data } = await api.get(`/invoices?${p.toString()}`);
    setInvoices(data);
  }, [clientId, status, from, to, q]);
  useEffect(() => { load(); }, [load]);
  const reloadAll = useCallback(() => { load(); onRefsReload && onRefsReload(); }, [load, onRefsReload]);

  const del = async (inv) => {
    if (!window.confirm(`Hapus invoice ${inv.invoice_no}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success(t("common.saved")); reloadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const [importOpen, setImportOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [sortDir, setSortDir] = useState("desc");
  useEffect(() => { setSel([]); }, [invoices]);
  const rows = [...invoices].sort((a, b) => (sortDir === "asc" ? 1 : -1) * String(a.date || "").localeCompare(String(b.date || "")));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = invoices.length > 0 && invoices.every((i) => sel.includes(i.id));
  const toggleAll = () => setSel(allOn ? [] : invoices.map((i) => i.id));
  const exportInv = () => {
    const list = sel.length ? rows.filter((i) => sel.includes(i.id)) : rows;
    exportRows(list, [["No", "invoice_no"], ["Tanggal", "date"], ["Jatuh Tempo", "due_date"], ["Pelanggan", (i) => i.bill_to?.name || ""], ["DPP", "dpp"], ["PPN", "ppn"],
      ["PPh", "pph"], ["Total", "total"], ["Terbayar", "paid_total"], ["Sisa", "balance_due"], ["Status", (i) => t((STATUS_META[i.status] || STATUS_META.unpaid).key)]], "invoice");
    toast.success(`Ekspor ${list.length} invoice`);
  };
  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf?lang=${lang}`, { responseType: "blob" });
      saveBlob(res.data, invoiceFileName(inv));
    } catch (e) { toast.error("Gagal unduh PDF"); }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder={t("inv.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 w-56" data-testid="invoice-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("inv.allStatus")}</SelectItem>
              <SelectItem value="unpaid">{t("inv.statusUnpaid")}</SelectItem>
              <SelectItem value="partial">{t("inv.statusPartial")}</SelectItem>
              <SelectItem value="paid">{t("inv.statusPaid")}</SelectItem>
            </SelectContent>
          </Select>
          <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" title={t("inv.from")} data-testid="invoice-from" />
          <DateInput value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" title={t("inv.to")} data-testid="invoice-to" />
          <Button variant="outline" size="sm" className="h-9" onClick={() => setSortDir((s) => (s === "desc" ? "asc" : "desc"))} data-testid="invoice-sort-date">
            {sortDir === "asc" ? <ArrowUpNarrowWide className="h-4 w-4 mr-1" /> : <ArrowDownWideNarrow className="h-4 w-4 mr-1" />}{sortDir === "asc" ? "Terlama" : "Terbaru"}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportInv} data-testid="invoice-export"><Download className="h-4 w-4 mr-1.5" />{sel.length ? `Ekspor terpilih (${sel.length})` : "Ekspor"}</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="invoice-import-btn"><Upload className="h-4 w-4 mr-1.5" />Import</Button>
          <InvoiceForm clientId={clientId} client={client} customers={customers} products={products} banks={banks} settings={settings} onDone={reloadAll} />
        </div>
      </div>
      <InvoiceImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} onImported={reloadAll} />
      <InvoiceBulkBar ids={sel} onClear={() => setSel([])} onDone={reloadAll} />
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><Checkbox checked={allOn} onCheckedChange={toggleAll} data-testid="invoice-select-all" /></TableHead>
            <TableHead>{t("inv.no")}</TableHead><TableHead>{t("common.date")}</TableHead><TableHead>{t("inv.customer")}</TableHead>
            <TableHead className="text-right">{t("inv.total")}</TableHead><TableHead className="text-right">{t("inv.paid")}</TableHead>
            <TableHead className="text-right">{t("inv.balanceDue")}</TableHead><TableHead>{t("common.status")}</TableHead>
            <TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {invoices.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground text-sm">{t("inv.noData")}</TableCell></TableRow>}
            {rows.map((inv) => {
              const m = STATUS_META[inv.status] || STATUS_META.unpaid;
              return (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell><Checkbox checked={sel.includes(inv.id)} onCheckedChange={() => toggle(inv.id)} data-testid={`invoice-select-${inv.id}`} /></TableCell>
                  <TableCell className="text-xs font-medium">{inv.invoice_no}
                    {inv.invoice_type && inv.invoice_type !== "tunai" && (
                      <span className="block text-[10px] font-normal text-muted-foreground" data-testid={`invoice-type-${inv.id}`}>{termLabel(inv)}{inv.ref_invoice_no ? ` · ref ${inv.ref_invoice_no}` : ""}</span>
                    )}
                  </TableCell>
                  <TableCell className="num text-xs">{fmtDate(inv.date)}</TableCell>
                  <TableCell className="text-sm">{inv.bill_to?.name || "—"}</TableCell>
                  <TableCell className="num text-right text-xs font-semibold">{fmtIDR(inv.total)}</TableCell>
                  <TableCell className="num text-right text-xs text-emerald-600">{fmtIDR(inv.paid_total)}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(inv.balance_due)}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${m.cls}`} data-testid={`invoice-status-${inv.id}`}>{t(m.key)}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <PaymentDialog inv={inv} onDone={load} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" title={t("inv.recordPayment")} data-testid={`invoice-pay-${inv.id}`}><Wallet className="h-4 w-4" /></Button>} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t("inv.downloadPdf")} onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.id}`}><FileType2 className="h-4 w-4" /></Button>
                    <InvoiceForm clientId={clientId} client={client} customers={customers} products={products} banks={banks} settings={settings} editInv={inv} onDone={reloadAll} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`invoice-edit-${inv.id}`}><Pencil className="h-4 w-4" /></Button>} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(inv)} data-testid={`invoice-del-${inv.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

const blankItem = () => ({ description: "", qty: 1, unit_price: 0, product_id: "", item_type: "jasa" });
const blankBillTo = () => ({ name: "", npwp: "", address: "", phone: "", email: "", code: "" });
const mapItem = (i) => ({ description: i.description, qty: i.qty, unit_price: i.unit_price, product_id: i.product_id || "", item_type: i.item_type === "barang" ? "barang" : "jasa" });

function InvoiceForm({ clientId, client, customers = [], products = [], banks = [], settings = {}, editInv, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const init = () => ({
    invoice_no: "", date: new Date().toISOString().slice(0, 10), due_date: "",
    issuer_type: "client",
    issuer: { name: client?.name || "", npwp: client?.npwp || "", address: client?.address || "" },
    bill_to: blankBillTo(), bank_account_id: settings?.default_bank_account_id || "",
    items: [blankItem()], discount: 0, ppn_rate: 0, pph_rate: 0, notes: "",
    invoice_type: "tunai", dp_amount: "", ref_invoice_no: "",
    signature_name: settings?.signature_name || "", signature_title: settings?.signature_title || "",
  });
  const [f, setF] = useState(init());
  const [termInfo, setTermInfo] = useState(null);
  const [autoNo, setAutoNo] = useState("");
  const busy = useRef(false);
  const fetchTerm = async (no) => {
    const { data } = await api.get(`/invoices/term-info?client_id=${clientId}&invoice_no=${encodeURIComponent(no)}&exclude_id=${editInv?.id || ""}`);
    setTermInfo(data);
    return data;
  };

  useEffect(() => {
    if (!open) return;
    setTermInfo(null);
    if (editInv) {
      setF({
        invoice_no: editInv.invoice_no, date: editInv.date, due_date: editInv.due_date || "",
        issuer_type: editInv.issuer_type || "client",
        issuer: { ...editInv.issuer }, bill_to: { ...blankBillTo(), ...editInv.bill_to },
        bank_account_id: editInv.bank_account_id || "",
        items: (editInv.items || []).map(mapItem),
        discount: editInv.discount, ppn_rate: editInv.ppn_rate, pph_rate: editInv.pph_rate, notes: editInv.notes || "",
        invoice_type: editInv.invoice_type || "tunai", dp_amount: editInv.dp_amount || "", ref_invoice_no: editInv.ref_invoice_no || "",
        signature_name: editInv.signature_name || "", signature_title: editInv.signature_title || "",
      });
      if (editInv.ref_invoice_no) fetchTerm(editInv.ref_invoice_no).catch(() => {});
    } else setF(init());
  }, [open, editInv]);

  useEffect(() => {
    if (!open || editInv || !clientId) return undefined;
    const tm = setTimeout(() => {
      const p = new URLSearchParams({ client_id: clientId, date: f.date || "", issuer_type: f.issuer_type, customer_name: f.bill_to.name || "", customer_code: f.bill_to.code || "" });
      api.get(`/invoices/next-no?${p.toString()}`).then(({ data }) => setAutoNo(data.invoice_no)).catch(() => {});
    }, 350);
    return () => clearTimeout(tm);
  }, [open, editInv, clientId, f.date, f.issuer_type, f.bill_to.name, f.bill_to.code]);

  const lookupRef = async () => {
    const no = (f.ref_invoice_no || "").trim();
    if (!no) { setTermInfo(null); return; }
    if (busy.current || (termInfo && termInfo.root.invoice_no === no)) return;
    busy.current = true;
    try {
      const data = await fetchTerm(no);
      const r = data.root;
      setF((s) => ({ ...s, ref_invoice_no: r.invoice_no, issuer_type: r.issuer_type || s.issuer_type, issuer: { ...s.issuer, ...(r.issuer || {}) },
        bill_to: { ...blankBillTo(), ...(r.bill_to || {}) }, bank_account_id: s.bank_account_id || r.bank_account_id || "",
        items: (r.items || []).map(mapItem), discount: r.discount || 0, ppn_rate: r.ppn_rate || 0, pph_rate: r.pph_rate || 0 }));
      toast.success(`Data invoice ${r.invoice_no} dimuat`);
    } catch (e) { setTermInfo(null); toast.error(apiErr(e.response?.data?.detail)); }
    finally { busy.current = false; }
  };

  const setIssuerType = (v) => {
    if (v === "savior") setF((s) => ({ ...s, issuer_type: v, issuer: { name: "SAVIOR Consulting", npwp: "", address: "" } }));
    else setF((s) => ({ ...s, issuer_type: v, issuer: { name: client?.name || "", npwp: client?.npwp || "", address: client?.address || "" } }));
  };
  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    if (c) setF((s) => ({ ...s, bill_to: { name: c.name, npwp: c.npwp || "", address: c.address || "", phone: c.phone || "", email: c.email || "", code: c.code || "" } }));
  };
  const addFromProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setF((s) => ({ ...s, items: [...s.items, { description: p.name, qty: 1, unit_price: Number(p.unit_price || 0), product_id: p.id, item_type: p.item_type || (p.track_stock ? "barang" : "jasa") }] }));
  };
  const setItem = (i, k, v) => setF((s) => ({ ...s, items: s.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, blankItem()] }));
  const rmItem = (i) => setF((s) => ({ ...s, items: s.items.filter((_, idx) => idx !== i) }));

  const subtotal = f.items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const jasaSub = f.items.filter((it) => it.item_type !== "barang").reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const disc = Number(f.discount || 0);
  const contract = subtotal - disc;
  const contractJasa = jasaSub - (subtotal ? disc * jasaSub / subtotal : 0);
  const child = f.invoice_type !== "tunai" && !!(f.ref_invoice_no || "").trim() && !!termInfo;
  const dpp = f.invoice_type === "dp" ? Number(f.dp_amount || 0) : f.invoice_type === "pelunasan" ? (termInfo ? termInfo.remaining : 0) : contract;
  const dppJasa = contract ? dpp * contractJasa / contract : 0;
  const ppn = Math.round(dpp * Number(f.ppn_rate || 0) / 100);
  const pph = Math.round(Math.max(0, dppJasa) * Number(f.pph_rate || 0) / 100);
  const total = dpp + ppn - pph;
  const dppLabel = f.invoice_type === "dp" ? "DPP DP / Termin" : f.invoice_type === "pelunasan" ? "DPP Pelunasan" : t("inv.dpp");

  const save = async () => {
    const payload = {
      client_id: clientId, invoice_no: f.invoice_no || "", date: f.date, due_date: f.due_date || "",
      issuer_type: f.issuer_type, issuer: f.issuer, bill_to: f.bill_to, bank_account_id: f.bank_account_id || "",
      items: f.items.filter((i) => i.description || i.unit_price).map((i) => ({ description: i.description, qty: Number(i.qty || 0), unit_price: Number(i.unit_price || 0), product_id: i.product_id || "", item_type: i.item_type === "barang" ? "barang" : "jasa" })),
      discount: Number(f.discount || 0), ppn_rate: Number(f.ppn_rate || 0), pph_rate: Number(f.pph_rate || 0), dp: Number(editInv?.dp || 0), notes: f.notes || "",
      invoice_type: f.invoice_type, dp_amount: Number(f.dp_amount || 0), ref_invoice_no: f.invoice_type === "tunai" ? "" : (f.ref_invoice_no || "").trim(),
      signature_name: f.signature_name || "", signature_title: f.signature_title || "",
    };
    try {
      if (editInv) await api.put(`/invoices/${editInv.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(t("common.saved")); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-invoice-btn"><Plus className="h-4 w-4 mr-2" />{t("inv.new")}</Button>}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{editInv ? t("inv.editTitle") : t("inv.new")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>{t("inv.no")}</Label><Input value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} placeholder={editInv ? "" : (autoNo ? `otomatis: ${autoNo}` : "otomatis")} data-testid="inv-no" /></div>
            <div className="space-y-1.5"><Label>{t("common.date")}</Label><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="inv-date" /></div>
            <div className="space-y-1.5"><Label>{t("inv.dueDate")}</Label><DateInput value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="inv-due" /></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="space-y-1.5"><Label>{t("inv.issuer")}</Label>
                <Select value={f.issuer_type} onValueChange={setIssuerType}>
                  <SelectTrigger data-testid="inv-issuer-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="client">{t("inv.issuerClient")}</SelectItem><SelectItem value="savior">{t("inv.issuerSavior")}</SelectItem></SelectContent>
                </Select>
              </div>
              <Input placeholder={t("common.name")} value={f.issuer.name} onChange={(e) => setF({ ...f, issuer: { ...f.issuer, name: e.target.value } })} data-testid="inv-issuer-name" />
              <Input placeholder={t("inv.npwp")} value={f.issuer.npwp} onChange={(e) => setF({ ...f, issuer: { ...f.issuer, npwp: e.target.value } })} data-testid="inv-issuer-npwp" />
              <Input placeholder={t("inv.address")} value={f.issuer.address} onChange={(e) => setF({ ...f, issuer: { ...f.issuer, address: e.target.value } })} data-testid="inv-issuer-address" />
            </div>
            <div className="space-y-2 rounded-md border border-border p-3">
              <Label>{t("inv.billTo")}</Label>
              {customers.length > 0 && (
                <Select onValueChange={pickCustomer}>
                  <SelectTrigger className="h-9" data-testid="inv-select-customer"><SelectValue placeholder={t("inv.selectCustomer")} /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Input className="col-span-2" placeholder={t("inv.customer")} value={f.bill_to.name} onChange={(e) => setF({ ...f, bill_to: { ...f.bill_to, name: e.target.value } })} data-testid="inv-billto-name" />
                <Input placeholder="Kode (mis. PJ)" value={f.bill_to.code || ""} onChange={(e) => setF({ ...f, bill_to: { ...f.bill_to, code: e.target.value.toUpperCase() } })} data-testid="inv-billto-code" />
              </div>
              <Input placeholder={t("inv.npwp")} value={f.bill_to.npwp} onChange={(e) => setF({ ...f, bill_to: { ...f.bill_to, npwp: e.target.value } })} data-testid="inv-billto-npwp" />
              <Input placeholder={t("inv.address")} value={f.bill_to.address} onChange={(e) => setF({ ...f, bill_to: { ...f.bill_to, address: e.target.value } })} data-testid="inv-billto-address" />
            </div>
          </div>

          <div className="space-y-1.5"><Label>{t("inv.bankAccount")}</Label>
            <Select value={f.bank_account_id || "none"} onValueChange={(v) => setF({ ...f, bank_account_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="inv-bank"><SelectValue placeholder={t("inv.selectBank")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("inv.noBankOnPdf")}</SelectItem>
                {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ` : ""}{b.name} · {b.bank_name} {b.account_number}{b.currency ? ` (${b.currency})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <InvoiceTermBox f={f} setF={setF} info={termInfo} onLookup={lookupRef} />
          {child && <p className="text-xs text-muted-foreground -mb-2" data-testid="inv-items-locked">Item, diskon & jenis barang/jasa mengikuti invoice DP pertama ({termInfo.root.invoice_no}).</p>}

          <div className="rounded-md border border-border overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("inv.items")}</TableHead><TableHead className="w-28">Jenis</TableHead><TableHead className="w-20 text-right">{t("inv.qty")}</TableHead>
                <TableHead className="w-36 text-right">{t("inv.unitPrice")}</TableHead><TableHead className="w-36 text-right">{t("inv.lineTotal")}</TableHead><TableHead className="w-8"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {f.items.map((it, i) => (
                  <TableRow key={i}>
                    <TableCell><Input value={it.description} disabled={child} onChange={(e) => setItem(i, "description", e.target.value)} className="h-8" data-testid={`inv-item-desc-${i}`} /></TableCell>
                    <TableCell>
                      <Select value={it.item_type === "barang" ? "barang" : "jasa"} onValueChange={(v) => setItem(i, "item_type", v)} disabled={child}>
                        <SelectTrigger className="h-8" data-testid={`inv-item-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="jasa">Jasa</SelectItem><SelectItem value="barang">Barang</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input type="number" value={it.qty} disabled={child} onChange={(e) => setItem(i, "qty", e.target.value)} className="num h-8 text-right" data-testid={`inv-item-qty-${i}`} /></TableCell>
                    <TableCell><Input type="number" value={it.unit_price} disabled={child} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="num h-8 text-right" data-testid={`inv-item-price-${i}`} /></TableCell>
                    <TableCell className="num text-right text-xs">{fmtIDR(Number(it.qty || 0) * Number(it.unit_price || 0))}</TableCell>
                    <TableCell>{f.items.length > 1 && !child && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmItem(i)}><X className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
                {!child && <TableRow><TableCell colSpan={6}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={addItem} data-testid="inv-add-item"><Plus className="h-3.5 w-3.5 mr-1" />{t("inv.addItem")}</Button>
                    {products.length > 0 && (
                      <Select onValueChange={addFromProduct} value="">
                        <SelectTrigger className="h-8 w-52" data-testid="inv-add-from-product"><SelectValue placeholder={t("inv.fromProduct")} /></SelectTrigger>
                        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtIDR(p.unit_price)}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                </TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("inv.discount")}</Label><Input type="number" className="num" value={f.discount} disabled={child} onChange={(e) => setF({ ...f, discount: e.target.value })} data-testid="inv-discount" /></div>
              <div />
              <div className="space-y-1.5"><Label>{t("inv.ppnRate")}</Label><Input type="number" className="num" value={f.ppn_rate} onChange={(e) => setF({ ...f, ppn_rate: e.target.value })} data-testid="inv-ppn-rate" /></div>
              <div className="space-y-1.5"><Label>{t("inv.pphRate")}</Label><Input type="number" className="num" value={f.pph_rate} onChange={(e) => setF({ ...f, pph_rate: e.target.value })} data-testid="inv-pph-rate" /></div>
              <div className="col-span-2 space-y-1.5"><Label>{t("inv.notes")}</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid="inv-notes" /></div>
              <div className="col-span-2 rounded-md border border-border p-2.5 space-y-2">
                <Label className="text-xs">{t("inv.signature")} <span className="text-muted-foreground font-normal">· kanan bawah PDF</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("inv.sigName")} value={f.signature_name} onChange={(e) => setF({ ...f, signature_name: e.target.value })} data-testid="inv-sig-name" />
                  <Input placeholder={t("inv.sigTitle")} value={f.signature_title} onChange={(e) => setF({ ...f, signature_title: e.target.value })} data-testid="inv-sig-title" />
                </div>
              </div>
            </div>
            <div className="rounded-md bg-secondary/40 p-3 space-y-1.5 text-sm">
              <SummaryRow label={t("inv.subtotal")} val={subtotal} />
              <SummaryRow label={t("inv.discount")} val={-Number(f.discount || 0)} />
              {f.invoice_type !== "tunai" && <SummaryRow label="Nilai Kontrak (DPP)" val={contract} />}
              {child && termInfo.used_dpp > 0 && <SummaryRow label="DP / Termin sebelumnya" val={-termInfo.used_dpp} />}
              <div className="font-medium text-foreground" data-testid="inv-dpp"><SummaryRow label={dppLabel} val={dpp} /></div>
              <SummaryRow label={t("inv.ppn")} val={ppn} />
              {Number(f.pph_rate || 0) > 0 && Math.round(dppJasa) !== Math.round(dpp) && <div data-testid="inv-dpp-jasa"><SummaryRow label="DPP Jasa (dasar PPh)" val={dppJasa} /></div>}
              <SummaryRow label={t("inv.pph")} val={-pph} />
              <div className="flex items-center justify-between border-t border-primary/40 pt-1.5 font-bold"><span>{t("inv.total")}</span><span className="num" data-testid="inv-total">{fmtIDR(total)}</span></div>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="inv-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, val }) {
  return <div className="flex items-center justify-between text-muted-foreground"><span>{label}</span><span className="num">{fmtIDR(val)}</span></div>;
}

function PaymentDialog({ inv, onDone, trigger }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), amount: "", note: "", bank_account_id: "" });
  const [banks, setBanks] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF({ date: new Date().toISOString().slice(0, 10), amount: inv.balance_due > 0 ? inv.balance_due : "", note: "", bank_account_id: inv.bank_account_id || "" });
    api.get(`/banks?client_id=${inv.client_id}`).then(({ data }) => {
      setBanks(data);
      setF((x) => ({ ...x, bank_account_id: x.bank_account_id || data[0]?.id || "" }));
    }).catch(() => {});
  }, [open, inv]);

  const add = async () => {
    if (!f.date) { toast.error("Tanggal bayar wajib diisi"); return; }
    if (!(Number(f.amount) > 0)) { toast.error("Nominal pembayaran harus lebih dari 0"); return; }
    if (!f.bank_account_id) { toast.error("Pilih rekening bank penerima"); return; }
    try {
      await api.post(`/invoices/${inv.id}/pay`, { date: f.date, amount: Number(f.amount || 0), note: f.note, bank_account_id: f.bank_account_id });
      toast.success("Pembayaran dicatat & pemasukan disetujui otomatis — Piutang (AR) langsung berkurang"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delPay = async (pid) => {
    try { await api.delete(`/invoices/${inv.id}/pay/${pid}`); toast.success(t("common.saved")); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("inv.recordPayment")} — {inv.invoice_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm rounded-md bg-secondary/40 p-2">
            <span>{t("inv.total")}: <b className="num">{fmtIDR(inv.total)}</b></span>
            <span>{t("inv.balanceDue")}: <b className="num">{fmtIDR(inv.balance_due)}</b></span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("inv.payDate")}</Label><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="pay-date" /></div>
            <div className="space-y-1.5"><Label>{t("inv.payAmount")}</Label><Input type="number" className="num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-amount" /></div>
          </div>
          <div className="space-y-1.5"><Label>{fl("payBank", lang)}</Label>
            <Select value={f.bank_account_id} onValueChange={(v) => setF({ ...f, bank_account_id: v })}>
              <SelectTrigger data-testid="pay-bank"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ${b.name}` : b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>{t("common.description")}</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="pay-note" /></div>
          <p className="text-[11px] text-muted-foreground" data-testid="pay-auto-note">{fl("payNote", lang)}</p>
          <Button onClick={add} className="w-full" data-testid="pay-save"><Plus className="h-4 w-4 mr-1" />{t("inv.recordPayment")}</Button>
          {inv.dp > 0 && <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border pb-1"><span>DP</span><span className="num">{fmtIDR(inv.dp)}</span></div>}
          {(inv.payments || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between text-xs border-b border-border pb-1">
              <span>{fmtDate(p.date)} {p.note && `· ${p.note}`}</span>
              <span className="flex items-center gap-2"><span className="num">{fmtIDR(p.amount)}</span><Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => delPay(p.id)} data-testid={`pay-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportBtn({ clientId, type, label, onDone, id }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-9" onClick={() => setOpen(true)} data-testid={`${id}-import`}><Upload className="h-4 w-4 mr-1" />Impor</Button>
      <MasterImportModal open={open} onOpenChange={setOpen} clientId={clientId} type={type} label={label} onImported={onDone} />
    </>
  );
}

const CUST_COLS = [["Nama", "name"], ["Kode", "code"], ["NPWP", "npwp"], ["Alamat", "address"], ["Telepon", "phone"], ["Email", "email"]];
const PROD_COLS = [["Nama", "name"], ["Satuan", "unit"], ["Harga", "unit_price"], ["Jenis", "item_type"], ["Kategori", "category"], ["Stok", "stock"], ["HPP Rata-rata", "avg_cost"]];

function CustomersTab({ clientId, customers, onDone }) {
  const { t } = useI18n();
  const tools = useTableTools(customers);
  const del = async (id) => { if (!window.confirm("Hapus pelanggan ini?")) return; await api.delete(`/invoice-customers/${id}`); toast.success(t("common.saved")); onDone(); };
  const delMany = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/invoice-customers/${id}`)); onDone(); return r; };
  return (
    <Card className="p-4" data-testid="inv-customers-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div><h2 className="font-head font-semibold">{t("inv.tabCustomers")} <span className="text-muted-foreground font-normal text-sm">({customers.length})</span></h2>
          <p className="text-xs text-muted-foreground">{t("inv.customersDesc")}</p></div>
        <CustomerForm clientId={clientId} onDone={onDone} />
      </div>
      <div className="mb-3"><TableToolbar tools={tools} id="customers" cols={CUST_COLS} filename="pelanggan" onDelete={delMany}>
        <ImportBtn clientId={clientId} type="customers" label={t("inv.tabCustomers")} onDone={onDone} id="customers" />
      </TableToolbar></div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><SelectAllBox tools={tools} id="customers" /></TableHead>
            <TableHead>{t("common.name")}</TableHead><TableHead>{t("inv.npwp")}</TableHead><TableHead>{t("inv.phone")}</TableHead><TableHead>{t("inv.email")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell><SelectBox tools={tools} row={c} id="customers" /></TableCell>
                <TableCell className="text-sm font-medium">{c.name}{c.code && <span className="ml-2 text-[10px] font-mono text-muted-foreground" data-testid={`customer-code-${c.id}`}>{c.code}</span>}</TableCell>
                <TableCell className="text-xs">{c.npwp || "—"}</TableCell>
                <TableCell className="text-xs">{c.phone || "—"}</TableCell>
                <TableCell className="text-xs">{c.email || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <CustomerForm clientId={clientId} editItem={c} onDone={onDone} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(c.id)} data-testid={`customer-del-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function CustomerForm({ clientId, editItem, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", code: "", npwp: "", address: "", phone: "", email: "" });
  useEffect(() => { if (open) setF(editItem ? { name: editItem.name, code: editItem.code || "", npwp: editItem.npwp || "", address: editItem.address || "", phone: editItem.phone || "", email: editItem.email || "" } : { name: "", code: "", npwp: "", address: "", phone: "", email: "" }); }, [open, editItem]);
  const save = async () => {
    try {
      const payload = { client_id: clientId, ...f };
      if (editItem) await api.put(`/invoice-customers/${editItem.id}`, payload);
      else await api.post("/invoice-customers", payload);
      toast.success(t("common.saved")); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-2" />{t("inv.newCustomer")}</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editItem ? t("common.edit") : t("inv.newCustomer")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>{t("common.name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="customer-name" /></div>
            <div className="space-y-1.5"><Label>Kode Pelanggan</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="mis. PJ" data-testid="customer-code" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">Kode dipakai di akhir nomor invoice: INV/KodePerusahaan/Tahun/Bulan/Urut-<b>Kode</b></p>
          <div className="space-y-1.5"><Label>{t("inv.npwp")}</Label><Input value={f.npwp} onChange={(e) => setF({ ...f, npwp: e.target.value })} data-testid="customer-npwp" /></div>
          <div className="space-y-1.5"><Label>{t("inv.address")}</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="customer-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("inv.phone")}</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="customer-phone" /></div>
            <div className="space-y-1.5"><Label>{t("inv.email")}</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="customer-email" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="customer-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab({ clientId, products, onDone }) {
  const { t, lang } = useI18n();
  const tools = useTableTools(products);
  const del = async (id) => { if (!window.confirm("Hapus produk/jasa ini?")) return; await api.delete(`/invoice-products/${id}`); toast.success(t("common.saved")); onDone(); };
  const delMany = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`/invoice-products/${id}`)); onDone(); return r; };
  return (
    <Card className="p-4" data-testid="inv-products-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div><h2 className="font-head font-semibold">{t("inv.tabProducts")} <span className="text-muted-foreground font-normal text-sm">({products.length})</span></h2>
          <p className="text-xs text-muted-foreground">{t("inv.productsDesc")}</p></div>
        <ProductForm clientId={clientId} onDone={onDone} />
      </div>
      <div className="mb-3"><TableToolbar tools={tools} id="products" cols={PROD_COLS} filename="produk_jasa" onDelete={delMany}>
        <ImportBtn clientId={clientId} type="products" label={t("inv.tabProducts")} onDone={onDone} id="products" />
      </TableToolbar></div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><SelectAllBox tools={tools} id="products" /></TableHead>
            <TableHead>{t("inv.productName")}</TableHead><TableHead>{fl("itemCat", lang)}</TableHead><TableHead>{t("inv.unit")}</TableHead><TableHead className="text-right">{t("inv.defaultPrice")}</TableHead><TableHead className="text-right">{t("inv.avgCost")}</TableHead><TableHead className="text-right">{t("inv.stock")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell><SelectBox tools={tools} row={p} id="products" /></TableCell>
                <TableCell className="text-sm font-medium">{p.name}</TableCell>
                <TableCell className="text-xs" data-testid={`product-cat-${p.id}`}>{p.track_stock ? fl(`cat_${p.category || "goods"}`, lang) : (p.item_type === "barang" ? fl("cat_goods", lang) : "Jasa")}</TableCell>
                <TableCell className="text-xs">{p.unit || "—"}</TableCell>
                <TableCell className="num text-right text-xs">{fmtIDR(p.unit_price)}</TableCell>
                <TableCell className="num text-right text-xs" data-testid={`product-cost-${p.id}`}>{p.track_stock ? fmtIDR(p.avg_cost) : "—"}</TableCell>
                <TableCell className="text-right text-xs">{p.track_stock ? <span className="num font-medium" data-testid={`product-stockval-${p.id}`}>{Number(p.stock || 0).toLocaleString("id-ID")}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {p.track_stock && <StockCardDialog clientId={clientId} product={p} />}
                  {p.track_stock && <StockDialog clientId={clientId} product={p} onDone={onDone} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" title={t("inv.manageStock")} data-testid={`product-stock-${p.id}`}><Boxes className="h-4 w-4" /></Button>} />}
                  <ProductForm clientId={clientId} editItem={p} onDone={onDone} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(p.id)} data-testid={`product-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ProductForm({ clientId, editItem, onDone, trigger }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", unit: "", unit_price: "", track_stock: false, item_type: "jasa", category: "goods" });
  useEffect(() => { if (open) setF(editItem ? { name: editItem.name, unit: editItem.unit || "", unit_price: editItem.unit_price ?? "", track_stock: !!editItem.track_stock, item_type: editItem.item_type || (editItem.track_stock ? "barang" : "jasa"), category: editItem.category || "goods" } : { name: "", unit: "", unit_price: "", track_stock: false, item_type: "jasa", category: "goods" }); }, [open, editItem]);
  const save = async () => {
    try {
      const payload = { client_id: clientId, name: f.name, unit: f.unit || "", unit_price: Number(f.unit_price || 0), track_stock: !!f.track_stock, item_type: f.track_stock ? "barang" : (f.item_type || "jasa"), category: f.track_stock ? f.category : "" };
      if (editItem) await api.put(`/invoice-products/${editItem.id}`, payload);
      else await api.post("/invoice-products", payload);
      toast.success(t("common.saved")); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-product-btn"><Plus className="h-4 w-4 mr-2" />{t("inv.newProduct")}</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editItem ? t("common.edit") : t("inv.newProduct")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{t("inv.productName")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("inv.unit")}</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="jam / unit / paket" data-testid="product-unit" /></div>
            <div className="space-y-1.5"><Label>{t("inv.defaultPrice")}</Label><Input type="number" className="num" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="product-price" /></div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label htmlFor="track-stock-switch" className="cursor-pointer">{t("inv.trackStock")}</Label>
            <Switch id="track-stock-switch" checked={!!f.track_stock} onCheckedChange={(v) => setF({ ...f, track_stock: v })} data-testid="product-track-stock" />
          </div>
          {f.track_stock && (
            <div className="space-y-1.5"><Label>{fl("itemCat", lang)}</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods">{fl("cat_goods", lang)}</SelectItem>
                  <SelectItem value="material">{fl("cat_material", lang)}</SelectItem>
                  <SelectItem value="finished">{fl("cat_finished", lang)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} data-testid="product-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StockDialog({ clientId, product, onDone, trigger }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [moves, setMoves] = useState([]);
  const [f, setF] = useState({ type: "purchase_in", qty: "", unit_cost: "", note: "", date: new Date().toISOString().slice(0, 10) });
  const load = async () => { const { data } = await api.get(`/stock-movements?product_id=${product.id}`); setMoves(data); };
  useEffect(() => { if (open) { load(); setF({ type: "purchase_in", qty: "", unit_cost: "", note: "", date: new Date().toISOString().slice(0, 10) }); } }, [open]);
  const add = async () => {
    if (!Number(f.qty)) { toast.error("Qty tidak boleh 0"); return; }
    try {
      await api.post("/stock-movements", { client_id: clientId, product_id: product.id, type: f.type, qty: Number(f.qty || 0), unit_cost: Number(f.unit_cost || 0), note: f.note, date: f.date });
      toast.success(t("common.saved")); load(); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/stock-movements/${id}`); toast.success(t("common.saved")); load(); onDone(); };
  const TYPE_LABEL = { purchase_in: t("inv.purchaseIn"), production_in: t("inv.productionIn"), production_out: fl("prodOut", lang), sale_out: t("inv.saleOut"), adjustment: t("inv.adjustment") };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{t("inv.manageStock")} — {product.name}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-6 text-sm rounded-md bg-secondary/40 p-2">
          <span>{t("inv.currentStock")}: <b className="num" data-testid="stock-current">{Number(product.stock || 0).toLocaleString("id-ID")}</b> {product.unit}</span>
          <span>{t("inv.avgCost")}: <b className="num">{fmtIDR(product.avg_cost)}</b></span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{t("inv.movementType")}</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="stock-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase_in">{t("inv.purchaseIn")}</SelectItem>
                <SelectItem value="production_in">{t("inv.productionIn")}</SelectItem>
                <SelectItem value="adjustment">{t("inv.adjustment")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal</Label><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="stock-date" /></div>
          <div className="space-y-1.5"><Label>Qty</Label><Input type="number" className="num" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="stock-qty" placeholder="mis. 10 atau -3" /></div>
          <div className="space-y-1.5"><Label>{t("inv.unitCost")}</Label><Input type="number" className="num" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} data-testid="stock-cost" /></div>
          <div className="col-span-2 space-y-1.5"><Label>{t("inv.notes")}</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="stock-note" /></div>
        </div>
        <Button onClick={add} data-testid="stock-save"><Plus className="h-4 w-4 mr-1" />{t("inv.addMovement")}</Button>
        <div className="overflow-auto rounded-md border border-border mt-2">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50"><TableHead>{t("common.date")}</TableHead><TableHead>{t("inv.movementType")}</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">{t("inv.unitCost")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {moves.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">{t("inv.noStock")}</TableCell></TableRow>}
              {moves.map((m) => (
                <TableRow key={m.id} data-testid={`stock-row-${m.id}`}>
                  <TableCell className="num text-xs">{fmtDate(m.date)}</TableCell>
                  <TableCell className="text-xs">{TYPE_LABEL[m.type] || m.type}{m.note ? ` · ${m.note}` : ""}</TableCell>
                  <TableCell className={`num text-right text-xs ${m.qty < 0 ? "text-rose-600" : "text-emerald-600"}`}>{Number(m.qty).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtIDR(m.unit_cost)}</TableCell>
                  <TableCell className="text-right">{(m.ref_type || "manual") === "manual" && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => del(m.id)} data-testid={`stock-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsTab({ clientId, settings, banks = [], onDone }) {
  const { t } = useI18n();
  const THEME_PRESETS = [
    { name: "Biru", v: "#1D4ED8" }, { name: "Hijau", v: "#059669" }, { name: "Teal", v: "#0D9488" },
    { name: "Merah Marun", v: "#9F1239" }, { name: "Ungu", v: "#6D28D9" }, { name: "Oranye", v: "#EA580C" },
    { name: "Slate", v: "#0F172A" }, { name: "Emas", v: "#B45309" },
  ];
  const [f, setF] = useState({ logo: "", footer_note: "", theme_color: "#1D4ED8", signature_image: "", signature_name: "", signature_title: "", default_bank_account_id: "" });
  useEffect(() => { setF({ logo: settings?.logo || "", footer_note: settings?.footer_note || "", theme_color: settings?.theme_color || "#1D4ED8",
    signature_image: settings?.signature_image || "", signature_name: settings?.signature_name || "", signature_title: settings?.signature_title || "",
    default_bank_account_id: settings?.default_bank_account_id || "" }); }, [settings]);
  const onSigFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Gambar terlalu besar (maks ~1MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((s) => ({ ...s, signature_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo terlalu besar (maks ~1MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setF((s) => ({ ...s, logo: reader.result }));
    reader.readAsDataURL(file);
  };
  const save = async () => {
    try {
      await api.put("/invoice-settings", { client_id: clientId, logo: f.logo || "", footer_note: f.footer_note || "", theme_color: f.theme_color || "#1D4ED8",
        signature_image: f.signature_image || "", signature_name: f.signature_name || "", signature_title: f.signature_title || "",
        default_bank_account_id: f.default_bank_account_id || "" });
      toast.success(t("common.saved")); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const hexOk = /^#[0-9A-Fa-f]{6}$/.test(f.theme_color || "");

  return (
    <Card className="p-4 space-y-4" data-testid="inv-settings-panel">
      <div><h2 className="font-head font-semibold">{t("inv.tabSettings")}</h2>
        <p className="text-xs text-muted-foreground">{t("inv.settingsDesc")}</p></div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t("inv.logo")}</Label>
          <div className="flex items-center gap-4">
            <div className="h-24 w-40 rounded-md border border-dashed border-border flex items-center justify-center bg-secondary/30 overflow-hidden">
              {f.logo ? <img src={f.logo} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
            </div>
            <div className="space-y-2">
              <Input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={onFile} data-testid="logo-file" className="max-w-xs" />
              {f.logo && <Button variant="outline" size="sm" onClick={() => setF((s) => ({ ...s, logo: "" }))} data-testid="logo-remove"><Trash2 className="h-4 w-4 mr-1" />{t("inv.removeLogo")}</Button>}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("inv.logoHint")}</p>
        </div>
        <div className="space-y-2">
          <Label>{t("inv.footerNote")}</Label>
          <Textarea rows={5} value={f.footer_note} onChange={(e) => setF({ ...f, footer_note: e.target.value })} placeholder="mis. Pembayaran paling lambat 14 hari setelah invoice diterbitkan." data-testid="settings-footer" />
          <Label className="pt-2 block">Rekening Bank Default</Label>
          <Select value={f.default_bank_account_id || "none"} onValueChange={(v) => setF({ ...f, default_bank_account_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="settings-default-bank"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Tanpa rekening default</SelectItem>
              {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ` : ""}{b.name} · {b.bank_name} {b.account_number}{b.currency ? ` (${b.currency})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">Otomatis terpilih di invoice baru & invoice hasil impor (bila kolom bank_account_no kosong).</p>
        </div>
      </div>

      <div className="space-y-2 border-t border-border pt-4">
        <Label>Warna Tema Invoice</Label>
        <p className="text-[11px] text-muted-foreground">Warna aksen pada PDF: judul "INVOICE", garis pembatas, dan header tabel.</p>
        <div className="flex flex-wrap gap-2" data-testid="inv-theme-presets">
          {THEME_PRESETS.map((p) => (
            <button key={p.v} type="button" onClick={() => setF((s) => ({ ...s, theme_color: p.v }))}
              title={p.name} data-testid={`inv-theme-${p.v.replace('#', '')}`}
              className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${(f.theme_color || "").toLowerCase() === p.v.toLowerCase() ? "border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30" : "border-border"}`}
              style={{ backgroundColor: p.v }} />
          ))}
        </div>
        <div className="flex items-center gap-3 pt-1">
          <input type="color" value={hexOk ? f.theme_color : "#1D4ED8"} onChange={(e) => setF({ ...f, theme_color: e.target.value })}
            className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer p-0.5" data-testid="inv-theme-color-input" />
          <Input value={f.theme_color} onChange={(e) => setF({ ...f, theme_color: e.target.value })}
            placeholder="#1D4ED8" className={`max-w-[160px] font-mono ${hexOk ? "" : "border-rose-400"}`} data-testid="inv-theme-hex" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">Pratinjau
              <span className="h-5 w-10 rounded" style={{ backgroundColor: hexOk ? f.theme_color : "#CBD5E1" }} /></span>
          </div>
        </div>
        {!hexOk && <p className="text-[11px] text-rose-500">Format warna harus hex 6 digit, mis. #1D4ED8</p>}
      </div>

      <div className="space-y-2 border-t border-border pt-4" data-testid="inv-signature-settings">
        <Label>{t("inv.signature")} (default)</Label>
        <p className="text-[11px] text-muted-foreground">Nama & jabatan otomatis terisi di invoice baru (tetap bisa diubah per invoice). Gambar tampil di atas garis tanda tangan.</p>
        <div className="grid md:grid-cols-3 gap-3 items-start">
          <Input placeholder={t("inv.sigName")} value={f.signature_name} onChange={(e) => setF({ ...f, signature_name: e.target.value })} data-testid="settings-sig-name" />
          <Input placeholder={t("inv.sigTitle")} value={f.signature_title} onChange={(e) => setF({ ...f, signature_title: e.target.value })} data-testid="settings-sig-title" />
          <div className="space-y-2">
            <Input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={onSigFile} data-testid="settings-sig-file" />
            {f.signature_image && <div className="flex items-center gap-2"><img src={f.signature_image} alt="ttd" className="h-12 object-contain border border-border rounded bg-white" data-testid="sig-preview" />
              <Button variant="outline" size="sm" onClick={() => setF((s) => ({ ...s, signature_image: "" }))} data-testid="sig-remove"><Trash2 className="h-4 w-4" /></Button></div>}
            <p className="text-[10px] text-muted-foreground">{t("inv.sigImage")}</p>
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={!hexOk} data-testid="settings-save">{t("inv.saveSettings")}</Button>
    </Card>
  );
}
